/**
 * Calendar of the selected period derived from the runway payload: the selected months (adjacent
 * or not), their day counts and offsets, labels. Day offsets run over the selected months only, so
 * a non-adjacent pick has no gaps (its pace chart is not drawn, see PacePanel).
 */
import type { Runway } from '@/api/types'
import { MONTH_ABBR, shortDate } from '@/lib/period'

const DAY_MS = 86_400_000

export interface QuarterCalendar {
  year: number
  /** 0-based month index of the period's first month. */
  startMonth: number
  /** Calendar months 1-12 of the period. */
  months: number[]
  monthLabels: string[]
  monthDays: number[]
  /** Day offset (from the period start) where each month begins. */
  monthStarts: number[]
  totalDays: number
  /** The months are adjacent. */
  contiguous: boolean
}

export function quarterCalendar(runway: Pick<Runway, 'period'>): QuarterCalendar {
  const { year, months, contiguous } = runway.period
  const monthDays = months.map((m) => new Date(Date.UTC(year, m, 0)).getUTCDate())
  const monthStarts = monthDays.map((_, i) => monthDays.slice(0, i).reduce((sum, days) => sum + days, 0))
  return {
    year,
    startMonth: (months[0] ?? 1) - 1,
    months: [...months],
    monthLabels: months.map((m) => MONTH_ABBR[m - 1] ?? ''),
    monthDays,
    monthStarts,
    totalDays: monthDays.reduce((sum, days) => sum + days, 0),
    contiguous,
  }
}

/** Parse `MM/DD/YYYY`. */
export function parseUsDate(value: string): { month: number; day: number; year: number } | null {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value.trim())
  if (!match) return null
  return { month: Number(match[1]), day: Number(match[2]), year: Number(match[3]) }
}

/** Zero-based day offset from the first day of the period, or null when outside it. */
export function dayOffset(calendar: QuarterCalendar, value: string): number | null {
  const parsed = parseUsDate(value)
  if (!parsed || parsed.year !== calendar.year) return null
  const index = calendar.months.indexOf(parsed.month)
  if (index < 0) return null
  const monthStart = Date.UTC(calendar.year, parsed.month - 1, 1)
  const day = Math.round((Date.UTC(parsed.year, parsed.month - 1, parsed.day) - monthStart) / DAY_MS)
  const offset = (calendar.monthStarts[index] ?? 0) + day
  return offset >= 0 && offset < calendar.totalDays ? offset : null
}

/** `Aug 5` for a zero-based day offset. */
export function labelForOffset(calendar: QuarterCalendar, offset: number): string {
  let remaining = offset
  for (let i = 0; i < calendar.months.length; i += 1) {
    const days = calendar.monthDays[i] ?? 0
    if (remaining < days) return `${calendar.monthLabels[i]} ${remaining + 1}`
    remaining -= days
  }
  return ''
}

/** 1-based calendar month number for a period month label (`Aug` → 8). */
export function monthNumber(calendar: QuarterCalendar, label: string): number | null {
  const index = calendar.monthLabels.indexOf(label)
  return index >= 0 ? (calendar.months[index] ?? null) : null
}

/** `Jul–Sep`, `Aug`, or `Jan, Feb, Aug` for months that are not adjacent. */
export function monthRange(calendar: QuarterCalendar): string {
  const labels = calendar.monthLabels
  if (labels.length <= 1) return labels[0] ?? ''
  if (!calendar.contiguous) return labels.join(', ')
  return `${labels[0]}–${labels[labels.length - 1]}`
}

/** `Sep 30`: the period's last day. */
export function quarterEndLabel(calendar: QuarterCalendar): string {
  const last = calendar.months.length - 1
  return `${calendar.monthLabels[last]} ${calendar.monthDays[last]}`
}

/** `Jul 1`: the period's first day. */
export function periodStartLabel(calendar: QuarterCalendar): string {
  return `${calendar.monthLabels[0]} 1`
}

/** "Q3 2026", or "Q3 2026 · as of Aug 14, 2026" when the as-of is dialled back. */
export function labelWithAsOf(runway: Pick<Runway, 'period'>): string {
  const { label, as_of: asOf, as_of_max: max } = runway.period
  return asOf < max ? `${label} · as of ${shortDate(asOf)}, ${asOf.slice(0, 4)}` : label
}
